import { useEffect, useState } from 'react'
import { Film, CheckCircle, AlertCircle, X, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  listFilmsForAdAssignment,
  listAds,
  assignAdToFilm,
  type AdvertiserFilmItem,
  type AdvertiserAdItem,
} from '@/lib/api/advertiser'

interface FilmRow extends AdvertiserFilmItem {
  pendingPrerollId?: string | null
  pendingDisplayId?: string | null
  dirty?: boolean
}

export function AdvertiserPlacements() {
  const [films, setFilms] = useState<FilmRow[]>([])
  const [ads, setAds] = useState<AdvertiserAdItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  async function reload() {
    try {
      setLoading(true)
      const [filmsRes, adsRes] = await Promise.all([
        listFilmsForAdAssignment({ limit: 200 }),
        listAds({ limit: 200 }),
      ])
      setFilms(
        filmsRes.items.map((f) => ({
          ...f,
          pendingPrerollId: f.prerollAdId ?? null,
          pendingDisplayId: f.displayAdId ?? null,
          dirty: false,
        })),
      )
      setAds(adsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  function setPreroll(filmId: string, adId: string | null) {
    setFilms((prev) =>
      prev.map((f) =>
        (f.filmId ?? f.id) === filmId
          ? { ...f, pendingPrerollId: adId, dirty: true }
          : f,
      ),
    )
  }

  function setDisplay(filmId: string, adId: string | null) {
    setFilms((prev) =>
      prev.map((f) =>
        (f.filmId ?? f.id) === filmId
          ? { ...f, pendingDisplayId: adId, dirty: true }
          : f,
      ),
    )
  }

  async function saveFilm(film: FilmRow) {
    const id = film.filmId ?? film.id ?? ''
    setSaving(id)
    try {
      await assignAdToFilm(id, {
        prerollAdId: film.pendingPrerollId ?? null,
        displayAdId: film.pendingDisplayId ?? null,
      })
      setNotice(`Placements saved for "${film.title ?? id}".`)
      setFilms((prev) =>
        prev.map((f) =>
          (f.filmId ?? f.id) === id
            ? {
                ...f,
                prerollAdId: film.pendingPrerollId ?? null,
                displayAdId: film.pendingDisplayId ?? null,
                dirty: false,
              }
            : f,
        ),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(null)
    }
  }

  const prerollAds = ads.filter((a) => a.type === 'preroll_video')
  const displayAds = ads.filter((a) => a.type === 'display_image')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Ad placements</h1>
        <p className="mt-1 text-sm text-gray-400">
          Assign pre-roll video and display image ads to individual films.
        </p>
      </div>

      {/* Notices */}
      {notice && (
        <div className="rounded-lg border border-green-700 bg-green-900/20 px-4 py-2 flex items-center gap-2 text-sm text-green-300">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-2 flex items-center gap-2 text-sm text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Info note */}
      <div className="rounded-lg border border-gray-800 bg-gray-900/30 px-4 py-3 text-xs text-gray-400">
        Changes are saved per-film. Premium subscribers will not see pre-roll ads regardless of
        assignment. Display ads appear as banners on the film detail page.
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse">Loading films…</p>
      ) : films.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-700 p-10 text-center space-y-3">
          <Film className="w-8 h-8 text-gray-600 mx-auto" />
          <p className="text-sm text-gray-400">No films available for ad assignment yet.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Film
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Pre-roll ad
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500 font-medium hidden md:table-cell">
                  Display ad
                </th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Save
                </th>
              </tr>
            </thead>
            <tbody>
              {films.map((film) => {
                const id = film.filmId ?? film.id ?? ''
                return (
                  <tr
                    key={id}
                    className={`border-b border-gray-900 transition-colors ${
                      film.dirty ? 'bg-[#D72638]/5' : 'hover:bg-white/[0.02]'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-white">{film.title ?? id}</p>
                        {film.genre && (
                          <p className="text-xs text-gray-500 capitalize">{film.genre}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={film.pendingPrerollId ?? ''}
                        onChange={(e) => setPreroll(id, e.target.value || null)}
                        className="w-full max-w-[220px] rounded-md border border-gray-700 bg-gray-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                      >
                        <option value="">— None —</option>
                        {prerollAds.map((a) => (
                          <option key={a.adId} value={a.adId}>
                            {a.name ?? a.adId}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <select
                        value={film.pendingDisplayId ?? ''}
                        onChange={(e) => setDisplay(id, e.target.value || null)}
                        className="w-full max-w-[220px] rounded-md border border-gray-700 bg-gray-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-[#D72638]"
                      >
                        <option value="">— None —</option>
                        {displayAds.map((a) => (
                          <option key={a.adId} value={a.adId}>
                            {a.name ?? a.adId}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        disabled={!film.dirty || saving === id}
                        onClick={() => saveFilm(film)}
                        className={`text-xs ${
                          film.dirty
                            ? 'bg-[#D72638] hover:bg-[#D72638]/90 text-white'
                            : 'bg-gray-800 text-gray-600 cursor-default'
                        }`}
                      >
                        {saving === id ? (
                          '…'
                        ) : (
                          <>
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
